<template>
  <section class="ncrm-privacy-card">
    <header class="ncrm-privacy-card__header">
      <h2 class="ncrm-privacy-card__title">{{ $t(`${copyKey}.title`) }}</h2>
      <p class="ncrm-privacy-card__subtitle">{{ $t(`${copyKey}.subtitle`) }}</p>
    </header>

    <div class="ncrm-privacy-pairs__stats">
      <div v-for="stat in stats" :key="stat.key" class="ncrm-privacy-pairs__stat" :style="{ '--stat-color': stat.color }">
        <span class="ncrm-privacy-pairs__stat-label">
          <span class="ncrm-privacy-dot" :style="{ background: stat.color }" />{{ stat.label }}
        </span>
        <span class="ncrm-privacy-pairs__stat-value">{{ formatNumber(stat.value) }}</span>
        <!-- Unitatea stă pe linia ei: fără acord gramatical cu numărul, care în
             RO/RU ar cere plurale pe care proiectul nu le folosește. -->
        <span class="ncrm-privacy-pairs__stat-unit">{{ stat.unit }}</span>
      </div>
    </div>

    <div class="ncrm-privacy-pairs__note">
      <v-icon size="18" class="ncrm-privacy-pairs__note-icon">mdi-information-outline</v-icon>
      <span>{{ $t("ncrm.contacts.dataProtection.workspace.legend") }}</span>
    </div>

    <template v-for="(group, index) in groups" :key="group.key">
      <v-divider v-if="index > 0" />
      <div class="ncrm-privacy-pairs__group">
        <div class="ncrm-privacy-pairs__group-head">
          <h3 class="ncrm-privacy-card__section">{{ $t(`${copyKey}.${group.key}`) }}</h3>
          <div v-if="index === 0 && legend.length" class="ncrm-privacy-legend">
            <span v-for="state in legend" :key="state" class="ncrm-privacy-legend__item">
              <span class="ncrm-privacy-dot" :style="{ background: colors[state] }" />{{ stateLabel(state) }}
            </span>
          </div>
        </div>
        <p v-if="group.unavailable" class="ncrm-privacy-card__muted">{{ $t(`${copyKey}.unavailable`) }}</p>
        <p v-else-if="!group.rows.length" class="ncrm-privacy-card__muted">{{ $t(`${copyKey}.empty`) }}</p>
        <div v-else class="ncrm-privacy-pairs__rows">
          <div v-for="row in group.rows" :key="row.key" class="ncrm-privacy-pairs__row">
            <span class="ncrm-privacy-pairs__label">{{ row.label }}</span>
            <NcrmPrivacyStackedBar
              :segments="segmentsOf(row)"
              :width-pct="row.widthPct"
              :aria-label="ariaOf(row)"
              show-values
            />
            <span class="ncrm-privacy-pairs__total" :title="$t(`${copyKey}.total`)">{{ row.total }}</span>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import NcrmPrivacyStackedBar from "./NcrmPrivacyStackedBar.vue";
import { PAIR_STATE_COLORS, pairLegendStates, pairRows, pairTotals } from "./privacyDashboardModel.mjs";
import { formatDisplayNumber } from "@/Utile/displayFormat.mjs";

export default {
  name: "NcrmPrivacyPairsCard",
  components: { NcrmPrivacyStackedBar },
  props: {
    byState: { type: Object, default: () => ({}) },
    // `null` = distribuție indisponibilă (contractul dashboard-ului), nu „fără perechi".
    byChannel: { type: Array, default: null },
    byPurpose: { type: Array, default: null },
    unavailable: { type: Array, default: () => [] },
  },
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.workspace.pairs"; },
    colors() { return PAIR_STATE_COLORS; },
    totals() { return pairTotals(this.byState); },
    stats() {
      const unit = this.$t(`${this.copyKey}.unit`);
      const parts = this.totals.negativeParts
        .map(({ state, count }) => `${this.stateLabel(state)}: ${count}`)
        .join(" · ");
      return [
        { key: "granted", label: this.$t(`${this.copyKey}.granted`), value: this.totals.granted, unit, color: PAIR_STATE_COLORS.granted },
        { key: "otherBasis", label: this.$t(`${this.copyKey}.otherBasis`), value: this.totals.other_basis, unit, color: PAIR_STATE_COLORS.other_basis },
        {
          key: "negative",
          label: this.$t(`${this.copyKey}.negative`),
          value: this.totals.negative,
          unit: parts || unit,
          color: PAIR_STATE_COLORS.refused,
        },
      ];
    },
    groups() {
      const channelLabel = (row) => this.$t(`ncrm.contacts.dataProtection.record.channels.${row.channel_code}`);
      return [
        {
          key: "byPurpose",
          unavailable: this.byPurpose === null || this.unavailable.includes("by_purpose"),
          rows: pairRows(this.byPurpose || [], "purpose_id", (row) => row.purpose_name),
        },
        {
          key: "byChannel",
          unavailable: this.byChannel === null || this.unavailable.includes("by_channel"),
          rows: pairRows(this.byChannel || [], "channel_code", channelLabel),
        },
      ];
    },
    legend() { return pairLegendStates(this.groups.map(({ rows }) => rows)); },
  },
  methods: {
    stateLabel(state) { return this.$t(`ncrm.contacts.dataProtection.record.states.${state}`); },
    segmentsOf(row) {
      return row.segments.map((segment) => ({
        key: segment.state,
        pct: segment.pct,
        value: segment.count,
        color: PAIR_STATE_COLORS[segment.state],
        title: `${this.stateLabel(segment.state)}: ${segment.count}`,
      }));
    },
    ariaOf(row) {
      return `${row.label}: ${row.segments.map((s) => `${this.stateLabel(s.state)} ${s.count}`).join(", ")}`;
    },
    formatNumber(value) { return formatDisplayNumber(Number(value || 0)); },
  },
};
</script>

<style scoped>
.ncrm-privacy-pairs__stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.ncrm-privacy-pairs__stat {
  display: flex; flex-direction: column; gap: 4px; padding: 14px 16px; border-radius: var(--app-radius-base);
  background: color-mix(in srgb, var(--stat-color) 9%, rgb(var(--v-theme-surface)));
}
.ncrm-privacy-pairs__stat-label { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; }
.ncrm-privacy-pairs__stat-value { font-size: 26px; font-weight: 700; color: var(--stat-color); line-height: 1.2; }
.ncrm-privacy-pairs__stat-unit { font-size: 12.5px; color: rgba(var(--v-theme-on-surface), 0.7); }
.ncrm-privacy-pairs__note {
  display: flex; gap: 10px; align-items: flex-start; padding: 12px 14px; border-radius: var(--app-radius-base);
  background: rgba(var(--v-theme-on-surface), 0.04); font-size: 13px; line-height: 1.45;
  color: rgba(var(--v-theme-on-surface), 0.78);
}
.ncrm-privacy-pairs__note-icon { color: rgb(var(--v-theme-quaternary)); margin-top: 1px; flex-shrink: 0; }
.ncrm-privacy-pairs__group { display: flex; flex-direction: column; gap: 12px; }
.ncrm-privacy-pairs__group-head { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 8px; }
.ncrm-privacy-pairs__rows { display: grid; grid-template-columns: 190px minmax(0, 1fr) 40px; gap: 10px 16px; align-items: center; }
.ncrm-privacy-pairs__label { font-size: 14px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ncrm-privacy-pairs__total { text-align: right; font-weight: 700; }
/* Pe desktop rândul nu există ca box: copiii lui intră direct în grila comună. */
.ncrm-privacy-pairs__row { display: contents; }
@media (max-width: 700px) {
  .ncrm-privacy-pairs__stats { grid-template-columns: 1fr; }
  /* `align-items: center` din grila de desktop ar centra și îngusta rândurile
     într-o coloană flex; pe mobil fiecare rând ocupă toată lățimea. */
  .ncrm-privacy-pairs__rows { display: flex; flex-direction: column; align-items: stretch; gap: 14px; }
  .ncrm-privacy-pairs__row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 6px 12px; }
  .ncrm-privacy-pairs__row > .ncrm-privacy-bar { grid-column: 1 / -1; grid-row: 2; }
}
</style>
